import React from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import SprintKanbanBoard from '../components/Sprints/SprintKanbanBoard';
import Card from '../../../ui/canon/Card';
import Button from '../../../ui/canon/Button';
import TaskDetailModal from '../components/AdvisorMap/TaskDetailModal';
import { CheckCircle2 } from 'lucide-react';

const VALUE_DASHBOARD_SPRINTS = gql`
  query ValueDashboardSprints($ownerUserId: ID) {
    sprints(owner_user_id: $ownerUserId) {
      id
      name
      status
      startOn
      endOn
      metricsSnapshot: metrics_snapshot
      items {
        id
        title
        description
        status
        priority
        dueAt
        assigneeUserId
        domain
        cepaCategory
        sprintIds
        sprintNames
        taskTemplate { key domain cepaCategory deadlineInDays }
      }
    }
  }
`;

const GET_CURRENT_USER_ID = gql`
  query GetCurrentUserIdForSprints {
    getCurrentUser { id }
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateActionPlanItem(
    $id: ID!
    $status: ActionPlanItemStatusEnum
    $priority: ActionPlanItemPriorityEnum
    $dueAt: ISO8601DateTime
    $assigneeUserId: ID
    $pipelineCardId: ID
    $title: String
    $description: String
  ) {
    update_action_plan_item(
      id: $id
      status: $status
      priority: $priority
      dueAt: $dueAt
      assigneeUserId: $assigneeUserId
      pipelineCardId: $pipelineCardId
      title: $title
      description: $description
    ) {
      id
      status
      priority
      dueAt
    }
  }
`;

const ValueAccelerationSprintContainer = ({ className = '' }) => {
  const { data: cuData } = useQuery(GET_CURRENT_USER_ID);
  const ownerUserId = cuData?.getCurrentUser?.id;
  const { data, loading, error, refetch } = useQuery(VALUE_DASHBOARD_SPRINTS, { variables: { ownerUserId }, skip: !ownerUserId });
  const [updateItem] = useMutation(UPDATE_ITEM);
  const [taskModalOpen, setTaskModalOpen] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState(null);

  if (loading) return <div className="text-sm text-gray-500">Loading sprint…</div>;
  if (error) return <div className="text-sm text-red-600">Error loading sprint</div>;

  const sprints = data?.sprints || [];
  const active = sprints.find((s) => s.status === 'ACTIVE');
  const fallback = sprints.slice().sort((a, b) => {
    const aStart = a?.startOn ? new Date(a.startOn).getTime() : 0;
    const bStart = b?.startOn ? new Date(b.startOn).getTime() : 0;
    return bStart - aStart || String(b?.id).localeCompare(String(a?.id));
  })[0];
  const sprint = active || fallback;

  const items = sprint?.items || [];
  const sprintHref = sprint?.id ? `/succession/sprint?sprintId=${encodeURIComponent(sprint.id)}` : '/succession/sprint';

  const handleItemUpdate = async (itemId, updates) => {
    try {
      const vars = { id: itemId };
      if (updates.status) vars.status = String(updates.status).toUpperCase();
      if (updates.priority) vars.priority = String(updates.priority).toUpperCase();
      if (updates.dueAt) vars.dueAt = updates.dueAt;
      if (updates.assigneeUserId) vars.assigneeUserId = updates.assigneeUserId;
      if (updates.pipelineCardId) vars.pipelineCardId = updates.pipelineCardId;
      if (updates.title !== undefined) vars.title = updates.title;
      if (updates.description !== undefined) vars.description = updates.description;
      await updateItem({ variables: vars });
      try { await refetch(); } catch (_) {}
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to update sprint item', e);
    }
  };

  const handleItemClick = (it) => {
    setSelectedTask(it);
    setTaskModalOpen(true);
  };

  return (
    <div className={`space-y-4 ${className}`} data-tutorial-id="sprint-card">
      <Card variant="outlined" size="md">
        {sprint ? (
          <>
            <SprintKanbanBoard
              items={items}
              maxPerColumn={3}
              seeMoreLinks={{ open: sprintHref, in_progress: sprintHref, completed: sprintHref }}
              onItemUpdate={handleItemUpdate}
              onItemClick={handleItemClick}
            />
            <div className="mt-4 pt-4 border-t border-succession-border px-6">
              <Button as="a" href={sprintHref} variant="primary" size="sm" className="w-full">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Manage Sprint Tasks
              </Button>
            </div>
          </>
        ) : (
          <div className="px-2 py-4 flex flex-col items-center text-center">
            <div className="max-w-xl">
              <h3 className="m-0 p-0 text-succession-foreground">No Active Sprint</h3>
              <p className="text-sm text-succession-text-secondary mt-1">
                Once you’ve completed your assessment, we’ll keep you on track with a focused, manageable set of tasks to complete every 90 days.
              </p>
              <div className="mt-3">
                <Button type="button" onClick={() => { try { if (window.ahoy?.track) window.ahoy.track('succession_readiness_start_assessment_clicked', { source: 'value_acceleration_sprint_empty' }); } catch(_) {} window.location.href = '/succession/plans'; }}>
                  Start Succession Plan
                </Button>
                <Button as="a" href="/succession/sprint" variant="ghost" className="ml-2">
                  Learn about Sprints
                </Button>
              </div>
            </div>
            <div className="w-full mt-6">
              <SprintKanbanBoard items={[]} isReadOnly className="" columnMinHeightClass="min-h-[28rem]" hideEmptyHint />
            </div>
          </div>
        )}
      </Card>
      <TaskDetailModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        task={selectedTask}
        onSave={async (id, updates) => { await handleItemUpdate(id, updates); setTaskModalOpen(false); }}
        onMarkCompleted={async (id) => { await handleItemUpdate(id, { status: 'completed' }); setTaskModalOpen(false); }}
        onDelete={async (_id) => { setTaskModalOpen(false); }}
      />
    </div>
  );
};

export default ValueAccelerationSprintContainer;


